import { isProtectedCardProduct } from '../../../../app/Libraries/ProductCardAvailability';

const PRODUCTION_HOSTS = ['www.raffi-jewellers.ca', 'raffi-jewellers.ca'];
const ROOT = 'main[data-collection-affirm] #collectionWrapper';
const escape = (value: unknown): string => String(value ?? '').replace(/[&<>"']/g, character => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[character] || character));

/** This opt-in is deliberately narrower than the shared catalogue-card guard. */
export function collectionAffirmEnabled(doc: Document = document, location: Pick<Location, 'hostname' | 'pathname'> = window.location): boolean {
  return PRODUCTION_HOSTS.includes(location.hostname)
    && /^\/(?:fr\/)?collections(?:\/[a-z0-9_-]+)*\/?$/i.test(location.pathname)
    && !isProtectedCardProduct({}, location.pathname)
    && !!doc.querySelector(ROOT);
}

export function collectionAffirmAmount(hit: any, path: string): number | null {
  const tags = Array.isArray(hit?.tags) ? hit.tags.map(tag => String(tag).trim().toLowerCase()) : [];
  const named = hit?.named_tags?.available;
  const quantity = hit?.inventory_quantity;
  const online = tags.includes('available:online') || (Array.isArray(named) ? named : [named]).some(value => typeof value === 'string' && value.trim().toLowerCase() === 'online');
  if (!hit || isProtectedCardProduct(hit, path) || !online || hit.inventory_available !== true
    || hit.hidePrice === true || hit.hide_price === true || tags.includes('hide-price') || tags.includes('inquiry-only')
    || ((typeof quantity === 'number' || (typeof quantity === 'string' && quantity.trim())) && Number.isFinite(Number(quantity)) && Number(quantity) <= 0)
    || typeof hit.price !== 'number' || !Number.isFinite(hit.price) || hit.price <= 0) return null;
  const cents = Math.round(hit.price * 100);
  return Number.isSafeInteger(cents) && cents > 0 ? cents : null;
}

/** Preserve the existing price/availability markup; attach raw data, never parse localized prices. */
export function collectionAffirmPrice(hit: any, markup: string, path: string, enabled: boolean): string {
  if (!enabled || !hit || isProtectedCardProduct(hit, path)) return markup;
  const amount = collectionAffirmAmount(hit, path);
  const data = { amount, sku: String(hit.sku || ''), brand: String(hit.named_tags?.brand || hit.vendor || hit.brand || ''),
    category: String(hit.product_type || ''), identity: `${hit.handle || ''}:${hit.objectID || ''}` };
  return markup.replace(/<p(?=\s)/, `<p data-collection-affirm-price="${escape(JSON.stringify(data))}"`);
}

let sequence = 0;
let initialized = false;
let ready = false;

/** Layout only: retain Affirm's exact wording, original price/logo/link and reading order.
 * Unknown provider structures remain untouched rather than guessing a financing message. */
export function formatCollectionAffirm(mount: HTMLElement): boolean {
  if (!mount.isConnected || mount.querySelector('.rf-affirm-monthly')) return false;
  const nodes = Array.from(mount.childNodes);
  const amountIndex = nodes.findIndex(node => node instanceof Element && node.matches('span.affirm-ala-price'));
  if (amountIndex < 1 || nodes.length !== amountIndex + 5) return false;
  const prefix = nodes.slice(0, amountIndex);
  const [amount, suffix, logo, punctuation, link] = nodes.slice(amountIndex);
  if (!prefix.every(node => node.nodeType === 3 || node instanceof Element && node.matches('span.__apr_badge'))
    || !amount.textContent?.trim() || suffix.nodeType !== 3 || punctuation.nodeType !== 3
    || !(logo instanceof Element) || !logo.matches('span.__affirm-logo')
    || !(link instanceof Element) || !link.matches('a.affirm-modal-trigger')
    || !/^\s*\.\s*$/.test(punctuation.textContent || '')) return false;
  const split = /^(\/(?:mo|mois))(\s+)(with|avec)(\s+)$/.exec(suffix.textContent || '');
  if (!split) return false;

  // Split the existing unit/connector text node at its original whitespace;
  // no characters are translated, replaced, calculated or dropped.
  const providerText = (suffix as Text).splitText(split[1].length + split[2].length);
  const terms = document.createElement('span'); terms.className = 'rf-affirm-terms';
  const monthly = document.createElement('span'); monthly.className = 'rf-affirm-monthly';
  const provider = document.createElement('span'); provider.className = 'rf-affirm-provider';
  mount.insertBefore(terms, prefix[0]); prefix.forEach(node => terms.appendChild(node));
  mount.insertBefore(monthly, amount); monthly.appendChild(amount); monthly.appendChild(suffix);
  mount.insertBefore(provider, providerText); provider.appendChild(providerText); provider.appendChild(logo); provider.appendChild(punctuation);
  return true;
}

export function initCollectionAffirm(): void {
  if (initialized || !collectionAffirmEnabled()) return;
  const root = document.querySelector<HTMLElement>(ROOT);
  if (!root) return;
  initialized = true;
  let scheduled = false;

  const hydrate = () => {
    scheduled = false;
    if (!root.isConnected || !collectionAffirmEnabled()) return;
    root.querySelectorAll<HTMLElement>('.collection__grid [data-signature-card]').forEach(card => {
      const price = card.querySelector<HTMLElement>('.product-card__price');
      if (!price || !card.isConnected) return;
      let wrapper = card.querySelector<HTMLElement>('.rf-collection-price');
      const raw = price.dataset.collectionAffirmPrice || '';
      // Clear an old estimate before any asynchronous work for the new selection.
      if (wrapper && wrapper.dataset.quoteKey !== raw) {
        wrapper.querySelector('.rf-collection-affirm')?.remove();
        delete wrapper.dataset.quoteKey;
      }
      if (!raw) return;
      let data: { amount: number | null; sku: string; brand: string; category: string; identity: string };
      try { data = JSON.parse(raw); } catch { return; }
      if (!wrapper) {
        wrapper = document.createElement('div');
        wrapper.className = 'rf-collection-price';
        price.before(wrapper);
        wrapper.appendChild(price);
      }
      if (data.identity !== `${card.dataset.product || ''}:${card.dataset.objectId || ''}`
        || typeof data.amount !== 'number' || !Number.isSafeInteger(data.amount) || data.amount <= 0 || !ready) return;
      if (wrapper.dataset.quoteKey === raw) {
        const existingMount = wrapper.querySelector<HTMLElement>('.rf-collection-affirm');
        if (existingMount) formatCollectionAffirm(existingMount);
        return;
      }
      const mount = document.createElement('div');
      mount.className = 'rf-collection-affirm';
      mount.id = `rf-collection-affirm-${++sequence}`;
      wrapper.appendChild(mount);
      wrapper.dataset.quoteKey = raw;
      try {
        // Official provider logo, amount, APR qualifiers and educational link remain intact.
        // A fresh mount per selection prevents late responses painting a different watch.
        const component = window.affirm!.ui.components.create('promo', {
          amount: Number(data.amount), pageType: 'category', affirmLogoType: 'logo', affirmLogoColor: 'black',
          sku: data.sku || undefined, brand: data.brand || undefined, category: data.category || undefined, learnMoreShow: true,
        });
        component.render(`#${mount.id}`);
        formatCollectionAffirm(mount);
      } catch {
        mount.remove();
        delete wrapper.dataset.quoteKey;
      }
    });
  };
  const schedule = () => { if (!scheduled) { scheduled = true; queueMicrotask(hydrate); } };
  const relevant = (node: Node) => node instanceof Element && (node.matches('.product-card__price, [data-signature-card]') || !!node.querySelector('.product-card__price'));
  new MutationObserver(records => {
    if (records.some(record => record.type === 'attributes' || [...record.addedNodes].some(relevant)
      || record.target instanceof Element && !!record.target.closest('.rf-collection-affirm'))) schedule();
  }).observe(root, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-collection-affirm-price'] });
  hydrate();
  window.affirm?.ui.ready(() => { ready = true; hydrate(); });
}
